import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileText, Eye } from "lucide-react";

export default function AdminDepositsTab() {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewing, setReviewing] = useState(null);
    const [notes, setNotes] = useState("");
    const [previewProof, setPreviewProof] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/deposits", { params: { status: tab === "all" ? undefined : tab } });
            setItems(data);
        } finally { setLoading(false); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [tab]);

    const approve = async (d) => {
        if (!window.confirm(`Aprobar recarga ${d.reference_code} por ${d.amount} ${d.currency}?`)) return;
        try {
            await api.post(`/admin/deposits/${d.id}/approve`, { notes: "" });
            toast.success("Recarga aprobada");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        }
    };

    const openReject = (d) => { setReviewing(d); setNotes(""); };
    const reject = async () => {
        if (!notes.trim()) { toast.error("Motivo requerido"); return; }
        try {
            await api.post(`/admin/deposits/${reviewing.id}/reject`, { notes });
            toast.success("Recarga rechazada");
            setReviewing(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        }
    };

    return (
        <div data-testid="admin-deposits-tab">
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-p2w-elevated border border-p2w-line">
                    <TabsTrigger value="pending" data-testid="depositstab-pending" className="data-[state=active]:bg-p2w-amber data-[state=active]:text-black">Pendientes</TabsTrigger>
                    <TabsTrigger value="approved" data-testid="depositstab-approved" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg">Aprobadas</TabsTrigger>
                    <TabsTrigger value="rejected" data-testid="depositstab-rejected" className="data-[state=active]:bg-p2w-red data-[state=active]:text-white">Rechazadas</TabsTrigger>
                    <TabsTrigger value="all" className="data-[state=active]:bg-p2w-blue data-[state=active]:text-white">Todas</TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-5">
                    <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden">
                        {loading ? (
                            <div className="p-8 text-center text-p2w-gray">Cargando…</div>
                        ) : items.length === 0 ? (
                            <div className="p-10 text-center text-p2w-gray text-sm">Sin recargas en esta vista.</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-p2w-elevated border-b border-p2w-line text-xs uppercase text-p2w-gray">
                                    <tr>
                                        <th className="text-left px-4 py-3">Ref</th>
                                        <th className="text-left px-4 py-3">Usuario</th>
                                        <th className="text-left px-4 py-3">Método</th>
                                        <th className="text-right px-4 py-3">Monto</th>
                                        <th className="text-left px-4 py-3">Evidencia</th>
                                        <th className="text-left px-4 py-3">Fecha</th>
                                        <th className="text-right px-4 py-3">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((d) => (
                                        <tr key={d.id} className="border-t border-p2w-line">
                                            <td className="px-4 py-3 font-mono text-xs text-white">{d.reference_code}</td>
                                            <td className="px-4 py-3 text-white">@{d.username}</td>
                                            <td className="px-4 py-3 text-white">{d.method === "bre_b" ? "Bre-B" : "USDT TRC20"}</td>
                                            <td className="px-4 py-3 text-right text-white number-tabular">{d.amount.toLocaleString("es-CO")} {d.currency}</td>
                                            <td className="px-4 py-3">
                                                {d.method === "bre_b" && d.proof_url ? (
                                                    <button onClick={() => setPreviewProof(d.proof_url)} data-testid={`view-proof-${d.id}`} className="inline-flex items-center gap-1 text-p2w-blue text-xs hover:underline">
                                                        <Eye className="w-3.5 h-3.5" /> Comprobante
                                                    </button>
                                                ) : d.tx_hash ? (
                                                    <span className="font-mono text-[10px] text-p2w-gray break-all">{d.tx_hash.slice(0, 22)}…</span>
                                                ) : (
                                                    <span className="text-p2w-gray">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-p2w-gray">{new Date(d.created_at).toLocaleString("es-CO")}</td>
                                            <td className="px-4 py-3 text-right">
                                                {d.status === "pending" ? (
                                                    <div className="flex gap-2 justify-end">
                                                        <Button size="sm" onClick={() => approve(d)} data-testid={`approve-${d.id}`} className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 h-7 px-3">
                                                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aprobar
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={() => openReject(d)} data-testid={`reject-${d.id}`} className="border-p2w-red/40 text-p2w-red bg-transparent hover:bg-p2w-red/10 hover:text-p2w-red h-7 px-3">
                                                            <XCircle className="w-3.5 h-3.5 mr-1" /> Rechazar
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <StatusPill s={d.status} />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={!!previewProof} onOpenChange={(o) => !o && setPreviewProof(null)}>
                <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-2xl">
                    <DialogHeader><DialogTitle>Comprobante</DialogTitle></DialogHeader>
                    {previewProof && (previewProof.startsWith("data:image") ? (
                        <img alt="Comprobante" src={previewProof} className="w-full max-h-[70vh] object-contain rounded-lg" />
                    ) : (
                        <div className="p-6 flex items-center gap-2 text-p2w-gray"><FileText className="w-5 h-5" /> Archivo subido (PDF/otro). Descarga manualmente.</div>
                    ))}
                </DialogContent>
            </Dialog>

            <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
                <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                    <DialogHeader><DialogTitle>Rechazar recarga</DialogTitle></DialogHeader>
                    <p className="text-sm text-p2w-gray">Indica el motivo. El usuario recibirá la notificación.</p>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="reject-notes" className="mt-2 bg-p2w-elevated border-p2w-line text-white" />
                    <DialogFooter className="mt-3">
                        <Button variant="outline" onClick={() => setReviewing(null)} className="border-p2w-line bg-transparent text-white">Cancelar</Button>
                        <Button onClick={reject} data-testid="reject-confirm" className="bg-p2w-red text-white hover:bg-p2w-red/90">Rechazar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatusPill({ s }) {
    const map = {
        pending: ["bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40", "Pendiente"],
        approved: ["bg-p2w-green/15 text-p2w-green border-p2w-green/40", "Aprobada"],
        rejected: ["bg-p2w-red/15 text-p2w-red border-p2w-red/40", "Rechazada"],
        approving: ["bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40", "Aprobando"],
    };
    const [cls, label] = map[s] || map.pending;
    return <span className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-wider border font-bold ${cls}`}>{label}</span>;
}
